import type { EventNotification, ResyncRequest } from './frames.js';

/**
 * What the client should do with an event it has just been handed.
 *
 * `duplicate` is normal and not a fault: delivery is at-least-once, so a relay that crashed between
 * publishing and marking published republishes. `gap` is also normal — a browser that was asleep,
 * a socket that was dropped — and is the signal to refetch the aggregate's snapshot rather than to
 * wait for events that are not coming.
 */
export type SequenceOutcome = 'applied' | 'duplicate' | 'gap';

/**
 * The last sequence the client has applied, per aggregate.
 *
 * It is the whole of the client's ordering logic and it is deliberately one small mutable object:
 * it survives a reconnection (that is its point — it is what `subscribe` and `resync` are built
 * from), and the alternative, a copy per reconnection, is a place for the two to disagree about
 * what the client has seen.
 */
export class SequenceTracker {
  private readonly last = new Map<string, number>();

  /** Decides what to do with an event, and records it where it is the newest the client has. */
  accept(notification: EventNotification, coalesced = false): SequenceOutcome {
    const known = this.last.get(notification.aggregateId);
    if (known !== undefined && notification.sequence <= known) {
      return 'duplicate';
    }
    // A coalesced frame stands for the ones the gateway withheld, so its jump is the dashboard rate
    // limit and not a lost event. Resyncing over it would ask for events nobody was going to send.
    const gap = known !== undefined && notification.sequence > known + 1 && !coalesced;
    this.last.set(notification.aggregateId, notification.sequence);
    return gap ? 'gap' : 'applied';
  }

  /** What the gateway told the client it is level with, after a replay. */
  seed(aggregateId: string, sequence: number): void {
    const known = this.last.get(aggregateId);
    if (known === undefined || known < sequence) {
      this.last.set(aggregateId, sequence);
    }
  }

  /** What the client already has, as `subscribe` carries it, so a resubscribe replays nothing. */
  lastSequences(): Record<string, number> {
    return Object.fromEntries(this.last);
  }

  /** What to ask a replay for: every aggregate the client follows, and where each one got to. */
  resyncRequests(): ResyncRequest[] {
    return [...this.last].map(([aggregateId, sinceSequence]) => ({ aggregateId, sinceSequence }));
  }

  /** Whether there is anything to catch up on, which is what decides a resync after a reconnect. */
  get isEmpty(): boolean {
    return this.last.size === 0;
  }

  /** Stops following an aggregate: an interaction that ended is not resynced for ever. */
  forget(aggregateId: string): void {
    this.last.delete(aggregateId);
  }
}
